<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
    isVisible: boolean;
    gameContext?: Record<string, unknown>;
}>();

const emit = defineEmits<{
    (e: 'close'): void;
}>();

const { t } = useI18n();

const category = ref('bug');
const message = ref('');
const isLoading = ref(false);
const sent = ref(false);

const categories = computed(() => [
    { value: 'bug', label: t('feedback.form.category_bug') },
    { value: 'feature', label: t('feedback.form.category_feature') },
    { value: 'comment', label: t('feedback.form.category_comment') },
    { value: 'other', label: t('feedback.form.category_other') },
]);

const submit = async () => {
    if (!message.value) return;
    isLoading.value = true;

    // Get API Base URL
    const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

    // Construct payload
    const payload = {
        category: category.value,
        message: message.value,
        metadata: {
            userAgent: navigator.userAgent,
            screen: `${window.screen.width}x${window.screen.height}`,
            window: `${window.innerWidth}x${window.innerHeight}`,
            url: window.location.href,
            gameContext: props.gameContext
        }
    };

    try {
        const response = await fetch(`${API_BASE}/feedback/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        });

        if (response.ok) {
            sent.value = true;
            setTimeout(() => {
                close();
            }, 2000);
        } else {
            console.error('Feedback submission failed', response);
            alert('送信に失敗しました。');
        }
    } catch (e) {
        console.error('Feedback error', e);
        alert('送信に失敗しました。');
    } finally {
        isLoading.value = false;
    }
};

const close = () => {
    emit('close');
    if (sent.value) {
        // Reset only if successfully sent and closed, wait for transition
        setTimeout(() => {
            sent.value = false;
            message.value = '';
            category.value = 'bug';
        }, 300);
    }
}
</script>

<template>
    <div v-if="isVisible" class="modal-overlay" @click.self="close">
        <div class="modal-content">
            <button class="close-icon" @click="close">×</button>

            <div v-if="sent" class="success-message">
                <div class="check-icon">✓</div>
                <h3>{{ t('feedback.form.success_title') }}</h3>
                <p>{{ t('feedback.form.success_message') }}</p>
            </div>

            <div v-else class="feedback-form">
                <h3>{{ t('feedback.title') }}</h3>
                <p class="description" v-html="t('feedback.description')"></p>

                <div class="form-group">
                    <label>{{ t('feedback.form.category_label') }}</label>
                    <div class="radio-group">
                        <label v-for="c in categories" :key="c.value" class="radio-label"
                            :class="{ active: category === c.value }">
                            <input type="radio" v-model="category" :value="c.value">
                            {{ c.label }}
                        </label>
                    </div>
                </div>

                <div class="form-group">
                    <label>{{ t('feedback.form.content_label') }}</label>
                    <textarea v-model="message" :placeholder="t('feedback.form.content_placeholder')"
                        rows="5"></textarea>
                </div>

                <button class="submit-btn" :disabled="isLoading || !message.trim()" @click="submit">
                    <span v-if="isLoading">{{ t('feedback.form.submitting') }}</span>
                    <span v-else>{{ t('feedback.form.submit') }}</span>
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    backdrop-filter: blur(2px);
}

.modal-content {
    background: white;
    padding: 2rem;
    /* 1.5rem -> 2rem */
    border-radius: 16px;
    /* 12px -> 16px */
    width: 90%;
    max-width: 500px;
    position: relative;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
    animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
    from {
        transform: translateY(20px);
        opacity: 0;
    }

    to {
        transform: translateY(0);
        opacity: 1;
    }
}

.close-icon {
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: #999;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s;
}

.close-icon:hover {
    background: #f0f0f0;
    color: #333;
}

h3 {
    margin-top: 0;
    margin-bottom: 0.5rem;
    color: #333;
    text-align: center;
}

.description {
    font-size: 0.9rem;
    color: #666;
    margin-bottom: 1.5rem;
    text-align: center;
    line-height: 1.4;
}

.form-group {
    margin-bottom: 1.2rem;
}

.form-group label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: bold;
    color: #444;
}

.radio-group {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.radio-label {
    background: #f5f5f5;
    padding: 0.4rem 0.8rem;
    border-radius: 20px;
    cursor: pointer;
    font-size: 0.9rem;
    border: 1px solid transparent;
    transition: all 0.2s;
}

.radio-label input {
    display: none;
}

.radio-label.active {
    background: #e6f7ff;
    border-color: #007bff;
    color: #007bff;
    font-weight: bold;
}

textarea {
    width: 100%;
    padding: 0.8rem;
    border: 1px solid #ddd;
    border-radius: 8px;
    resize: vertical;
    font-family: inherit;
    font-size: 1rem;
}

textarea:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.submit-btn {
    width: 100%;
    padding: 0.8rem;
    background: linear-gradient(135deg, #007bff, #0056b3);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: bold;
    cursor: pointer;
    transition: transform 0.1s, opacity 0.2s;
}

.submit-btn:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
}

.submit-btn:not(:disabled):hover {
    opacity: 0.9;
    transform: translateY(-1px);
}

.success-message {
    text-align: center;
    padding: 2rem 0;
}

.check-icon {
    width: 60px;
    height: 60px;
    background: #28a745;
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    margin: 0 auto 1rem;
}
</style>
